import { useId, useState } from 'react';

/**
 * Staff testing tools (shown only when `/api/me` says staff): fill the page with the sample, or
 * empty it. Each asks once inline before it sends, and both wait while one is in flight.
 */
type Action = 'fill' | 'clear';

const COPY: Record<Action, { button: string; confirm: string; warning: string; pending: string }> = {
  fill: { button: 'Fill with sample profile', confirm: 'Fill', warning: 'This replaces your certifications, jobs and awards.', pending: 'Filling…' },
  clear: { button: 'Clear profile', confirm: 'Clear', warning: 'This removes everything except your name and photo, and hides the page.', pending: 'Clearing…' },
};

export function TestingTools({ onFill, onClear }: { onFill(): Promise<void>; onClear(): Promise<void> }) {
  const id = useId();
  const [confirming, setConfirming] = useState<Action | null>(null);
  const [pending, setPending] = useState<Action | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const run = async (action: Action) => {
    setConfirming(null);
    setPending(action);
    setFailure(null);
    try { await (action === 'fill' ? onFill() : onClear()); } catch { setFailure('That didn’t work. Try again.'); } finally { setPending(null); }
  };
  return <section className="testing-tools" aria-labelledby={`${id}-h`}>
    <h2 id={`${id}-h`}>Testing — staff only</h2>
    <div className="testing-tools-actions">
      {(['fill', 'clear'] as const).map((action) => confirming === action
        ? <span key={action} className="confirm-remove">{COPY[action].warning}{' '}
          <button type="button" className="text-button danger" onClick={() => void run(action)}>{COPY[action].confirm}</button>{' '}
          <button type="button" className="text-button" onClick={() => setConfirming(null)}>Cancel</button>
        </span>
        : <button key={action} type="button" className="text-button" disabled={pending !== null} onClick={() => setConfirming(action)}>
          {pending === action ? COPY[action].pending : COPY[action].button}
        </button>)}
    </div>
    {failure && <p className="field-error" role="alert">{failure}</p>}
  </section>;
}
